import { HostSmsStatus } from '@/apis/business/setting/phoneNumbers/types';

export const STATUS_PRESENTATION: Record<HostSmsStatus, { label: string; bg: string; color: string }> = {
  draft: { label: 'Draft', bg: '#F3F4F6', color: '#6B7280' },
  otp_verified: { label: 'Number Verified', bg: '#FAE8FF', color: '#A21CAF' },
  received: { label: 'Order Received', bg: '#E0F2FE', color: '#0369A1' },
  processing: { label: 'Processing', bg: '#FEF9C3', color: '#A16207' },
  partial: { label: 'Partial', bg: '#FFEDD5', color: '#C2410C' },
  failed: { label: 'Failed', bg: '#FEE2E2', color: '#DC2626' },
  complete: { label: 'Complete', bg: '#DCFCE7', color: '#15803D' },
  disconnecting: { label: 'Disconnecting', bg: '#F3F4F6', color: '#6B7280' },
  disconnected: { label: 'Disconnected', bg: '#F3F4F6', color: '#6B7280' }
};

export const statusPresentation = (status?: string | null) =>
  STATUS_PRESENTATION[status as HostSmsStatus] || { label: status || '-', bg: '#F3F4F6', color: '#6B7280' };

// Bandwidth reports progress on these through webhooks, so the UI refreshes while they last.
export const IN_PROGRESS_STATUSES: HostSmsStatus[] = ['received', 'processing', 'disconnecting'];
export const HOST_SMS_POLL_INTERVAL_MS = 15000;

export const PROVIDER_OPTIONS = [
  { value: 'verizon', label: 'Verizon' },
  { value: 'att', label: 'AT&T' },
  { value: 'tmobile', label: 'T-Mobile' },
  { value: 'twilio', label: 'Twilio' },
  { value: 'bandwidth', label: 'Bandwidth' },
  { value: 'other', label: 'Other' }
];

export const providerLabel = (provider?: string | null) =>
  PROVIDER_OPTIONS.find((option) => option.value === provider)?.label || provider || '';

export const COUNTRY_OPTIONS = [
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' }
];

const US_STATES: [string, string][] = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['DC', 'District of Columbia'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming']
];

const CA_PROVINCES: [string, string][] = [
  ['AB', 'Alberta'],
  ['BC', 'British Columbia'],
  ['MB', 'Manitoba'],
  ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'],
  ['NT', 'Northwest Territories'],
  ['NS', 'Nova Scotia'],
  ['NU', 'Nunavut'],
  ['ON', 'Ontario'],
  ['PE', 'Prince Edward Island'],
  ['QC', 'Quebec'],
  ['SK', 'Saskatchewan'],
  ['YT', 'Yukon']
];

const toOptions = (entries: [string, string][]) => entries.map(([value, label]) => ({ value, label }));

export const STATE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  US: toOptions(US_STATES),
  CA: toOptions(CA_PROVINCES)
};

export const LOA_ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.tif,.tiff,.csv,.xls,.xlsx';

export const formatHostSmsNumber = (phoneNumber?: string | null) => {
  if (!phoneNumber) return '';
  const digits = phoneNumber.replace(/\D/g, '');
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits.slice(-10);
  if (national.length !== 10) return phoneNumber;
  return `+1 ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6)}`;
};

export const formatAddedOn = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export const apiErrorMessage = (error: unknown, fallback: string) =>
  (error as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;
